/**
 * 収穫予定一覧タブ管理
 *
 * === Public ===
 * fetchAndPopulateEstimatedReturnTabs  - シーン遷移時にJSON取得→タブ描画
 * selectEstimatedReturnTab             - タブクリック時のハンドラー
 *
 * === Private ===
 * updateEstimatedReturnTabStyles       - 選択状態のスタイル更新
 * updateEstimatedReturnFrameSrc        - Turbo Frameのsrc設定
 * renderEstimatedReturnTabs            - タブのDOM生成（テンプレートクローン方式）
 * buildEstimatedReturnTabMeta          - タブメタデータを構築
 */

import axios from "@/modules/axios/index";

export const EstimatedReturnTabsManagementMixin = {
  // === Public ===

  /**
   * シーン遷移時にJSON取得→タブ描画
   * switchToSceneHarvestEstimatedReturn() から呼び出される
   */
  async fetchAndPopulateEstimatedReturnTabs() {
    const now = new Date();
    const startMonth = now.getMonth() + 1; // 例: 2月 → getMonth()+1 → 2
    const startYear = now.getFullYear();

    // Build tab metadata (month labels + params for Turbo Frame)
    this._estimatedReturnTabMeta = this.buildEstimatedReturnTabMeta(startYear, startMonth);

    // Fetch totals from JSON endpoint
    try {
      const response = await axios.get(this.estimatedReturnTotalsPathValue, {
        params: { start_month: startMonth, start_year: startYear },
      });

      const { grand, ...tabTotals } = response.data;

      // Store totals for tab rendering
      this._estimatedReturnTotals = tabTotals;

      // Populate grand total
      if (this.hasEstimatedReturnGrandTotalTarget) {
        this.estimatedReturnGrandTotalTarget.textContent = this.formatAmountWithCurrency(grand);
      }
    } catch {
      // On error, show zero totals
      this._estimatedReturnTotals = {
        current: 0,
        next: 0,
        beyond: 0,
        grand: 0,
      };
      if (this.hasEstimatedReturnGrandTotalTarget) {
        this.estimatedReturnGrandTotalTarget.textContent = this.formatAmountWithCurrency(0);
      }
    }

    // Render tabs and select first
    this.renderEstimatedReturnTabs();
    const firstTabIndex = 0;
    this._selectedEstimatedReturnTabIndex = firstTabIndex;
    this.updateEstimatedReturnTabStyles(firstTabIndex);
    this.updateEstimatedReturnFrameSrc(firstTabIndex);
  },

  /**
   * タブクリック時のハンドラー
   * data-action="click->harvest-warehouse#selectEstimatedReturnTab"
   */
  selectEstimatedReturnTab(event) {
    const index = parseInt(event.currentTarget.dataset.tabIndex, 10);
    if (index === this._selectedEstimatedReturnTabIndex) return;

    this._selectedEstimatedReturnTabIndex = index;
    this.updateEstimatedReturnTabStyles(index);
    this.updateEstimatedReturnFrameSrc(index);
  },

  // === Private ===

  /**
   * 選択状態のスタイル更新
   * 呼び出し元: fetchAndPopulateEstimatedReturnTabs, selectEstimatedReturnTab
   * @param {number} selectedIndex - 選択されたタブのインデックス
   */
  updateEstimatedReturnTabStyles(selectedIndex) {
    this.estimatedReturnTabTargets.forEach((tab, i) => {
      tab.toggleAttribute("data-selected", i === selectedIndex);
    });
  },

  /**
   * Turbo Frameのsrcを設定し、コンテンツを自動再読み込みさせる
   * 呼び出し元: fetchAndPopulateEstimatedReturnTabs, selectEstimatedReturnTab
   * @param {number} tabIndex - タブのインデックス
   */
  updateEstimatedReturnFrameSrc(tabIndex) {
    if (!this.hasEstimatedReturnListFrameTarget || !this.estimatedReturnListPathValue) return;

    const meta = this._estimatedReturnTabMeta[tabIndex];
    const params = new URLSearchParams({
      year: meta.year,
      month: meta.month,
      ...(meta.ongoing && { ongoing: true }),
    });

    this.estimatedReturnListFrameTarget.src = `${this.estimatedReturnListPathValue}?${params}`;
  },

  /**
   * タブのDOM生成（テンプレートクローン方式）
   * 呼び出し元: fetchAndPopulateEstimatedReturnTabs
   */
  renderEstimatedReturnTabs() {
    if (!this.hasEstimatedReturnTabTemplateTarget || !this.hasEstimatedReturnTabsTarget) return;

    const template = this.estimatedReturnTabTemplateTarget;
    const tabsContainer = this.estimatedReturnTabsTarget;

    const totals = this._estimatedReturnTotals || {};
    const totalKeys = ["current", "next", "beyond"];

    // Clear existing tabs
    tabsContainer.innerHTML = "";

    this._estimatedReturnTabMeta.forEach((meta, index) => {
      const tab = template.content.firstElementChild.cloneNode(true);
      const label = tab.querySelector(".harvest-warehouse-estimated-return__tab-label");
      const amount = tab.querySelector(".harvest-warehouse-estimated-return__tab-amount");

      if (label) label.textContent = meta.label;
      if (amount) {
        amount.textContent = this.formatAmountWithCurrency(totals[totalKeys[index]] || 0);
      }

      tab.dataset.tabIndex = index;
      tabsContainer.appendChild(tab);
    });
  },

  /**
   * タブメタデータを構築（今月・来月・再来月以降の3タブ）
   * 呼び出し元: fetchAndPopulateEstimatedReturnTabs
   * @param {number} startYear
   * @param {number} startMonth - 1-indexed month (1=January, 12=December)
   * @returns {Array<{label: string, year: number, month: number, ongoing: boolean}>}
   */
  buildEstimatedReturnTabMeta(startYear, startMonth) {
    const startMonthIndex = startMonth - 1;

    const monthAt = (monthsAhead) => {
      const date = new Date(startYear, startMonthIndex + monthsAhead);
      return { year: date.getFullYear(), month: date.getMonth() + 1 };
    };

    const current = monthAt(0);
    const next = monthAt(1);
    const beyond = monthAt(2);

    return [
      { ...current, label: `${current.month}月`, ongoing: false },
      { ...next, label: `${next.month}月`, ongoing: false },
      { ...beyond, label: `${beyond.month}月以降`, ongoing: true },
    ];
  },
};
